import { createSlice } from '@reduxjs/toolkit';
import { IStaffUI } from 'types/staff';
import actions from '../actions/shop';
import { ISalonInfoResData, IScheduleBooking } from 'services/types/shop';
import { ICategoryUI } from 'types/category';
import { IServiceUI } from 'types/service';
import { ICustomerUI } from 'types/customer';
import { ISchedulerSalonResData, IStaffResItem } from 'services/types/response';
import { IBlockHourHistoriesResponse, IBlockHourResItem } from 'services/types/blockHours';
import set from 'lodash/set';
import { STAFF_CALENDAR_PAGE_SIZE } from 'constants/calendar';
import { IStaffGroupUI } from 'types/staff-group';

const _scheduleBooking = {
  ['Sun']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Sat']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Fri']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Thu']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Wed']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Tue']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Mon']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
};

const NAME_REDUCER = 'shop';

export type IStateShop = {
  staffs: IStaffUI[],
  pureStaffs: IStaffUI[],
  staffsObject: Record<string, IStaffUI>,
  staffsAvailable: IStaffUI[],
  blockHourHistories: IBlockHourHistoriesResponse,
  timeOffList: IBlockHourResItem[],
  timeOffListLoading: boolean,
  scheduleBooking: IScheduleBooking | null,
  salonWorking: ISchedulerSalonResData | null,
  categories: ICategoryUI[],
  allServices: IServiceUI[],
  customers: ICustomerUI[],
  setting: {
    verifyPinChangeAppointment: boolean;
    verifyPinCancelAppointment: boolean;
    enableCallerV2: boolean;
    enableAppointmentRepeat: boolean;
    appointmentStyleA: boolean;
    allowUndoCheckIn: boolean;
    sameDayBookingCheck: boolean;
    appointmentTicketBadge: boolean;
    quickBookingGroupAssign: boolean;
  },
  salonInfo: ISalonInfoResData | null;
  clockInStaffs: IStaffResItem[];
  enableCalendarPaging: boolean;
  staffGroups: IStaffGroupUI[];
  /** staffId -> its group. Derived on success so components never scan the array. */
  staffGroupsByStaffId: Record<string, IStaffGroupUI>;
}
const initialState: IStateShop = {
  staffs: [],
  pureStaffs: [],
  staffsObject: {},
  staffsAvailable: [],
  blockHourHistories: { data: [], page: 1, pageSize: 20, total: 0 },
  timeOffList: [],
  timeOffListLoading: false,
  scheduleBooking: _scheduleBooking,
  salonWorking: null,
  categories: [],
  allServices: [],
  customers: [],
  setting: {
    verifyPinChangeAppointment: false,
    verifyPinCancelAppointment: false,
    enableAppointmentRepeat: false,
    enableCallerV2: false,
    appointmentStyleA: false,
    allowUndoCheckIn: false,
    sameDayBookingCheck: false,
    appointmentTicketBadge: false,
    quickBookingGroupAssign: false,
  },
  salonInfo: null,
  clockInStaffs: [],
  enableCalendarPaging: false,
  staffGroups: [],
  staffGroupsByStaffId: {},
};

export const Slice = createSlice({
  name: NAME_REDUCER,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(actions.clockInStaffs.fetchPaging, (state) => {
        state.clockInStaffs = [];
      })
      .addCase(actions.clockInStaffs.success, (state, { payload }) => {
        state.clockInStaffs = payload;
      })
      .addCase(actions.salonInfo.success, (state, { payload }) => {
        state.salonInfo = payload;
        // Go check-in xoa ticket trong POS, nen mo dan theo tiem thay vi bat cho tat ca.
        // Chua co SystemConf key nen van phai allowlist theo shop id o day.
        state.setting.allowUndoCheckIn = ['251', '192'].includes(payload.id.toString() || '');
      })
      .addCase(actions.shopAllSetting.success, (state, { payload }) => {
        // Backend value is authoritative on every load — re-derive so a persisted local value can't shadow it.
        state.setting.verifyPinChangeAppointment = !!payload.verifyPinChangeAppointment;
        state.setting.verifyPinCancelAppointment = !!payload.verifyPinCancelAppointment;
        state.setting.enableCallerV2 = !!payload.enableCallerV2;
        state.setting.enableAppointmentRepeat = !!payload.enableAppointmentRepeat;
        state.setting.appointmentTicketBadge = !!payload.appointmentTicketBadge;
        state.setting.quickBookingGroupAssign = !!payload.quickBookingGroupAssign;
        state.setting.sameDayBookingCheck = !!payload.sameDayBookingCheck;
        state.setting.appointmentStyleA = !!payload.appointmentStyleA;
      })
      .addCase(actions.pureStaffs.success, (state, { payload }) => {
        state.pureStaffs = payload;
      })
      .addCase(actions.scheduler.success, (state, { payload }) => {
        state.salonWorking = payload;
        const _scheduleBooking = {
          ['Sun']: {
            enable: !!payload.sunday,
            endTime: payload.sunEndTime,
            startTime: payload.sunStartTime,
          },
          ['Sat']: {
            enable: !!payload.saturday,
            endTime: payload.satEndTime,
            startTime: payload.satStartTime,
          },
          ['Fri']: {
            enable: !!payload.friday,
            endTime: payload.friEndTime,
            startTime: payload.friStartTime,
          },
          ['Thu']: {
            enable: !!payload.thursday,
            endTime: payload.thuEndTime,
            startTime: payload.thuStartTime,
          },
          ['Wed']: {
            enable: !!payload.wednesday,
            endTime: payload.wedEndTime,
            startTime: payload.wedStartTime,
          },
          ['Tue']: {
            enable: !!payload.tuesday,
            endTime: payload.tueEndTime,
            startTime: payload.tueStartTime,
          },
          ['Mon']: {
            enable: !!payload.monday,
            endTime: payload.monEndTime,
            startTime: payload.monStartTime,
          },
        };
        state.scheduleBooking = _scheduleBooking;
      })
      .addCase(actions.category.success, (state, { payload }) => {
        state.categories = payload;
        const services: IServiceUI[] = [];
        state.categories.map(cate => {
          cate.services.map(s => {
            services.push({
              ...s,
              cateId: cate.id,
            });
          });
        });
        state.allServices = services;
      })
      .addCase(actions.staffGroups.success, (state, { payload }) => {
        state.staffGroups = payload || [];
        const byStaffId: Record<string, IStaffGroupUI> = {};
        state.staffGroups.forEach(group => {
          (group.staffIds || []).forEach(staffId => {
            byStaffId[staffId] = group;
          });
        });
        state.staffGroupsByStaffId = byStaffId;
      })
      .addCase(actions.staffAvailable.success, (state, { payload }) => {
        state.staffsAvailable = payload || [];
        state.enableCalendarPaging = state.staffsAvailable.length > STAFF_CALENDAR_PAGE_SIZE;
      })
      .addCase(actions.blockHourHistories.success, (state, { payload }) => {
        state.blockHourHistories = payload || { data: [], page: 1, pageSize: 20, total: 0 };
      })
      .addCase(actions.timeOffList.fetch, (state) => {
        state.timeOffListLoading = true;
      })
      .addCase(actions.timeOffList.success, (state, { payload }) => {
        state.timeOffList = payload || [];
        state.timeOffListLoading = false;
      })
      .addCase(actions.timeOffList.fail, (state) => {
        state.timeOffListLoading = false;
      })
      .addCase(actions.staff.success, (state, { payload }) => {
        state.staffs = payload;
        const staffsObject = {};
        payload.map((o: IStaffUI) => {
          set(staffsObject, o.id, o);
        });
        state.staffsObject = staffsObject;
      })
      .addCase(actions.customer.success, (state, { payload }) => {
        state.customers = payload;
      })
      .addCase(actions.afterCreateCustomer, (state, { payload }) => {
        state.customers.pop();
        state.customers.unshift(payload);
      })
      .addCase(actions.afterUpdateCustomer, (state, { payload }) => {
        state.customers = state.customers.map(o => {
          if (o.id?.toString() === payload?.id?.toString()) {
            return ({
              ...o,
              ...payload,
              id: o.id,
            });
          }
          return o;
        });
      })
      .addCase(actions.afterCreateService, (state, { payload }) => {
        state.allServices.unshift(payload);
        state.categories = state.categories.map(o => {
          if (o.id?.toString() === payload.cateId?.toString()) return ({
            ...o,
            services: [payload, ...o.services],
          });
          return o;
        });
      })
      ;
  },
});

const shopReducer = Slice.reducer;
export default shopReducer;
